/**
 * Read-only Make It Safe live-NOW adapter.
 *
 * GET only. Never mints OE1/OIK1/OI1, never writes incidents/lifecycle,
 * never PUT-policies, never resolves/clears product state.
 *
 * Active join class: INCIDENT_ID_INTERSECTION.
 * Scope (camera/zone/policy) resolves exactly one site condition.
 * It does not select the active incident.
 *
 * LIVE_STATE_AUTHORITY = fresh qualifying membership only.
 *   CLEAR        ← qualifying_present false after the physical clear dwell
 *   OBSERVING    ← qualifying_present true before the entry dwell
 *   ACTIVE       ← qualifying_present true once the entry dwell is met,
 *                  or the physical clear dwell after occupancy was observed
 *   UNAVAILABLE  ← membership unread. Workflow OPEN is never substituted.
 *
 * WORKFLOW_STATE_AUTHORITY = the review case (Needs review / Assigned /
 * In review / Resolved). It may outlive the physical event. It never selects
 * CLEAR, OBSERVING, or ACTIVE.
 *
 * Generic /zones/{zone}/status occupancy is not a phase input.
 */

import type { BuyerLiveItem } from '../incidents/liveAlertsReconciliation';
import type { Zone } from '../../services/zonesApi';

export const SHOWCASE_ACTIVE_JOIN_CLASS = 'INCIDENT_ID_INTERSECTION';
export const SHOWCASE_JOIN_KEY = 'incident_id';
export const SHOWCASE_SCOPE_HEURISTIC_USED_FOR_ACTIVE = 'NO';
export const SHOWCASE_OBSERVING_SOURCE = 'condition-membership';
export const SHOWCASE_ZONE_STATUS_USED_FOR_PHASE = 'NO';

export type ShowcaseLivePhase = 'clear' | 'observing' | 'active' | 'unknown';

export type ShowcaseLiveNowConfig = {
  siteId: string | null;
  cameraId: string | null;
  zoneId: string | null;
  policyId: string | null;
  deviceId: string | null;
};

export type ShowcaseMembershipRead = {
  readOk: boolean;
  qualifyingPresent: boolean | null;
  qualifyingCount: number | null;
};

export type ShowcaseConditionIdentity = {
  conditionId: string;
  cameraIds?: string[] | null;
  zoneIds?: string[] | null;
  policyIds?: string[] | null;
};

export type ShowcaseConditionSelection =
  | { status: 'selected'; conditionId: string }
  | { status: 'unavailable' };

export type ShowcaseOpenIncidentSelection =
  | { status: 'none' }
  | { status: 'active'; incident: BuyerLiveItem }
  | { status: 'unavailable' };

export type ShowcaseLiveNowSnapshot = {
  phase: ShowcaseLivePhase;
  liveCanonicalActive: boolean;
  occupancy: number | null;
  occupancyReadOk: boolean;
  membershipReadOk: boolean;
  qualifyingPresent: boolean | null;
  buyerLiveReadOk: boolean;
  thresholdSeconds: number | null;
  dwellElapsedSeconds: number | null;
  zonePolygon: number[][] | null;
  zoneId: string | null;
  cameraId: string | null;
  matchingIncidentId: string | null;
  /** Linked operational incident workflow. Independent of the physical phase. */
  workflowStatus?: 'OPEN' | null;
  /** How many linked workflow cases are still open. Does not select phase. */
  workflowOpenCount?: number;
  areaName: string | null;
  /** Identity selected for this booth scope. Not a live measurement. */
  conditionId?: string | null;
};

export const SHOWCASE_LIVE_PHASE_LABEL: Record<ShowcaseLivePhase, string> = {
  clear: 'CLEAR',
  observing: 'OBSERVING',
  active: 'CONDITION ACTIVE',
  unknown: 'UNAVAILABLE',
};

export const SHOWCASE_NOW_ACTIVE_CONDITION = 'Restricted-area entry';
export const SHOWCASE_NOW_QUIET_DETAIL = 'No condition active';
export const SHOWCASE_NOW_OBSERVING_DETAIL = 'Zone occupied';
export const SHOWCASE_NOW_UNAVAILABLE_DETAIL = 'Condition state unavailable';
export const SHOWCASE_ZONE_CLEAR_DWELL_SECONDS = 10;

/** Visitor copy only. Does not select phase or join incidents. */
export function visitorNowHeadline(phase: ShowcaseLivePhase): string {
  return SHOWCASE_LIVE_PHASE_LABEL[phase] || SHOWCASE_LIVE_PHASE_LABEL.unknown;
}

/** Visitor copy only. Does not select phase or join incidents. */
export function visitorNowDetail(phase: ShowcaseLivePhase): string {
  if (phase === 'active') return SHOWCASE_NOW_ACTIVE_CONDITION;
  if (phase === 'observing') return SHOWCASE_NOW_OBSERVING_DETAIL;
  if (phase === 'clear') return SHOWCASE_NOW_QUIET_DETAIL;
  return SHOWCASE_NOW_UNAVAILABLE_DETAIL;
}

export const SHOWCASE_THRESHOLD_NARRATION =
  'The policy says this becomes a condition after the configured threshold.';

/** Visitor workflow copy. Does not select CLEAR / OBSERVING / ACTIVE. */
export function workflowReviewLabel(openCount: number): string | null {
  const count = Math.floor(Number(openCount) || 0);
  if (count <= 0) return null;
  if (count === 1) return '1 case needs review';
  return `${count} cases need review`;
}

export const LIVE_STATE_AUTHORITY = 'fresh-qualifying-membership';
export const WORKFLOW_STATE_AUTHORITY = 'incident-review-case';

function text(value: unknown): string {
  return String(value || '').trim();
}

export function parsePositiveSeconds(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function envShowcaseLiveDefaults(): ShowcaseLiveNowConfig & { thresholdSeconds: number | null } {
  const env = typeof process !== 'undefined' ? process.env : undefined;
  return {
    siteId: null,
    cameraId: text(env?.REACT_APP_SHOWCASE_BOOTH_CAMERA_ID) || null,
    zoneId: text(env?.REACT_APP_SHOWCASE_BOOTH_ZONE_ID) || null,
    policyId: text(env?.REACT_APP_SHOWCASE_BOOTH_POLICY_ID) || null,
    deviceId: text(env?.REACT_APP_SHOWCASE_BOOTH_DEVICE_ID) || null,
    thresholdSeconds: parsePositiveSeconds(env?.REACT_APP_SHOWCASE_THRESHOLD_SECONDS),
  };
}

/** Static booth identity key. Not a live measurement. Device is membership scope, not identity join. */
export function showcaseIdentityConfigKey(config: Pick<ShowcaseLiveNowConfig, 'siteId' | 'cameraId' | 'zoneId' | 'policyId'>): string {
  return [text(config.siteId), text(config.cameraId), text(config.zoneId), text(config.policyId)].join('|');
}

/** Membership read scope. Changing device/camera/zone must not reuse the previous probe. */
export function showcaseMembershipScopeKey(config: Pick<ShowcaseLiveNowConfig, 'siteId' | 'deviceId' | 'cameraId' | 'zoneId'>): string {
  return [text(config.siteId), text(config.deviceId), text(config.cameraId), text(config.zoneId)].join('|');
}

export function idsEqual(left: unknown, right: unknown): boolean {
  const a = text(left);
  const b = text(right);
  return Boolean(a) && Boolean(b) && a === b;
}

function scopeIncludes(values: string[] | null | undefined, expected: string): boolean {
  return (values || []).some((value) => idsEqual(value, expected));
}

/** Booth camera/zone/policy selects exactly one identity-only condition. Not the active incident. */
export function selectSiteConditionForBoothScope(args: {
  conditions: ShowcaseConditionIdentity[] | null | undefined;
  cameraId: string | null;
  zoneId: string | null;
  policyId: string | null;
}): ShowcaseConditionSelection {
  const cameraId = text(args.cameraId);
  const zoneId = text(args.zoneId);
  const policyId = text(args.policyId);
  if (!cameraId && !zoneId && !policyId) return { status: 'unavailable' };
  const matches = (Array.isArray(args.conditions) ? args.conditions : []).filter((condition) => {
    const conditionId = text(condition?.conditionId);
    if (!conditionId) return false;
    if (cameraId && !scopeIncludes(condition.cameraIds, cameraId)) return false;
    if (zoneId && !scopeIncludes(condition.zoneIds, zoneId)) return false;
    if (policyId && !scopeIncludes(condition.policyIds, policyId)) return false;
    return true;
  });
  if (matches.length !== 1) return { status: 'unavailable' };
  return { status: 'selected', conditionId: text(matches[0].conditionId) };
}

/** Explicit condition-lifecycle incidents[].incident_id only. */
export function linkedIncidentIdsForConditionLifecycle(lifecycle: {
  incidents?: Array<{ incident_id?: string | null }> | null;
} | null | undefined): string[] {
  const incidents = lifecycle?.incidents;
  const rows = Array.isArray(incidents) ? incidents : [];
  const ids: string[] = [];
  const seen = new Set<string>();
  rows.forEach((incident) => {
    const id = text(incident?.incident_id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

export function buyerLiveIncidentId(item: BuyerLiveItem | null | undefined): string {
  return text(item?.operational?.incident_id);
}

export function selectOpenIncidentForCondition(args: {
  linkedIncidentIds: string[] | null | undefined;
  buyerLiveIncidents: BuyerLiveItem[] | null | undefined;
}): ShowcaseOpenIncidentSelection {
  const linked = new Set((args.linkedIncidentIds || []).map((id) => text(id)).filter(Boolean));
  const matches: BuyerLiveItem[] = [];
  const seen = new Set<string>();
  (Array.isArray(args.buyerLiveIncidents) ? args.buyerLiveIncidents : []).forEach((item) => {
    const id = buyerLiveIncidentId(item);
    if (!id || !linked.has(id) || seen.has(id)) return;
    seen.add(id);
    matches.push(item);
  });
  if (matches.length === 0) return { status: 'none' };
  if (matches.length === 1) return { status: 'active', incident: matches[0] };
  return { status: 'unavailable' };
}

export function deriveShowcaseLivePhase(args: {
  openMatch: boolean;
  joinUnavailable?: boolean;
  identityReadOk?: boolean;
  lifecycleReadOk?: boolean;
  buyerLiveReadOk: boolean;
  membershipReadOk: boolean;
  qualifyingPresent: boolean | null;
  /** Seconds the current fresh occupancy has lasted. Null when this sample has no anchor. */
  occupiedForSeconds?: number | null;
  /** Seconds the current fresh-empty streak has lasted. Null on a single sample. */
  emptyForSeconds?: number | null;
  entryDwellSeconds?: number | null;
  clearDwellSeconds?: number | null;
  /** True after occupancy already passed the entry dwell, so a false sample is a clear dwell. */
  clearingFromOccupancy?: boolean;
}): ShowcaseLivePhase {
  if (!args.membershipReadOk || typeof args.qualifyingPresent !== 'boolean') {
    return 'unknown';
  }
  if (args.qualifyingPresent) {
    const occupied = args.occupiedForSeconds;
    const entry = args.entryDwellSeconds;
    if (entry != null && occupied != null && occupied >= entry) return 'active';
    return 'observing';
  }
  const clearing = Boolean(args.clearingFromOccupancy);
  if (!clearing) return 'clear';
  const clearDwell = args.clearDwellSeconds ?? SHOWCASE_ZONE_CLEAR_DWELL_SECONDS;
  const empty = args.emptyForSeconds;
  if (empty != null && clearDwell > 0 && empty >= clearDwell) return 'clear';
  return 'active';
}

export function membershipFromProbe(args: {
  payload: unknown;
  deviceId: string;
  cameraId: string;
  zoneId: string;
}): ShowcaseMembershipRead {
  const unread: ShowcaseMembershipRead = {
    readOk: false,
    qualifyingPresent: null,
    qualifyingCount: null,
  };
  const payload = args.payload as Record<string, unknown> | null | undefined;
  if (!payload || typeof payload !== 'object') return unread;
  if (payload.read_ok !== true) return unread;
  if (typeof payload.qualifying_present !== 'boolean') return unread;
  if (text(payload.device_id) && !idsEqual(payload.device_id, args.deviceId)) return unread;
  if (text(payload.camera_id) && !idsEqual(payload.camera_id, args.cameraId)) return unread;
  if (text(payload.zone_id) && !idsEqual(payload.zone_id, args.zoneId)) return unread;
  const countRaw = payload.qualifying_count;
  const count = countRaw == null || countRaw === '' ? null : Number(countRaw);
  return {
    readOk: true,
    qualifyingPresent: payload.qualifying_present,
    qualifyingCount: count != null && Number.isFinite(count) ? count : null,
  };
}

export function resolveThresholdSeconds(args: {
  zone?: Pick<Zone, 'min_dwell_seconds' | 'dwell_alert_seconds'> | null;
  envSeconds?: number | null;
}): number | null {
  const fromZone = parsePositiveSeconds(args.zone?.min_dwell_seconds)
    ?? parsePositiveSeconds(args.zone?.dwell_alert_seconds);
  if (fromZone != null) return fromZone;
  return parsePositiveSeconds(args.envSeconds);
}

export function polygonFromZone(zone: Zone | null | undefined): number[][] | null {
  const poly = zone?.polygon_norm;
  if (!Array.isArray(poly) || poly.length < 3) return null;
  const points = poly
    .map((pair) => [Number(pair?.[0]), Number(pair?.[1])] as [number, number])
    .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
  return points.length >= 3 ? points : null;
}

function linkedOpenWorkflowIncidents(args: {
  identityReadOk: boolean;
  lifecycleReadOk: boolean;
  buyerLiveReadOk: boolean;
  linkedIncidentIds: string[] | null;
  buyerLiveIncidents: BuyerLiveItem[] | null | undefined;
}): BuyerLiveItem[] {
  if (!args.identityReadOk || !args.lifecycleReadOk || !args.buyerLiveReadOk) return [];
  if (!Array.isArray(args.linkedIncidentIds)) return [];
  const linked = new Set(args.linkedIncidentIds.map((id) => text(id)).filter(Boolean));
  const matches: BuyerLiveItem[] = [];
  const seen = new Set<string>();
  (Array.isArray(args.buyerLiveIncidents) ? args.buyerLiveIncidents : []).forEach((item) => {
    const id = buyerLiveIncidentId(item);
    if (!id || !linked.has(id) || seen.has(id)) return;
    seen.add(id);
    matches.push(item);
  });
  return matches;
}

export function dwellElapsedSeconds(args: {
  occupancyStartedAtMs: number | null;
  nowMs: number;
  occupancy: number | null;
}): number | null {
  if (args.occupancy == null || args.occupancy <= 0) return null;
  if (args.occupancyStartedAtMs == null) return 0;
  return Math.max(0, (args.nowMs - args.occupancyStartedAtMs) / 1000);
}

export function assembleLiveNowSnapshot(args: {
  config: ShowcaseLiveNowConfig;
  items: BuyerLiveItem[] | null | undefined;
  buyerLiveReadOk: boolean;
  identityReadOk: boolean;
  lifecycleReadOk: boolean;
  linkedIncidentIds: string[] | null;
  zone: Zone | null | undefined;
  membership: ShowcaseMembershipRead | null | undefined;
  envThresholdSeconds?: number | null;
  occupancyStartedAtMs: number | null;
  emptySinceMs?: number | null;
  clearingFromOccupancy?: boolean;
  nowMs?: number;
  conditionId?: string | null;
}): ShowcaseLiveNowSnapshot {
  const nowMs = args.nowMs ?? Date.now();
  const workflowIncidents = linkedOpenWorkflowIncidents({
    identityReadOk: args.identityReadOk,
    lifecycleReadOk: args.lifecycleReadOk,
    buyerLiveReadOk: args.buyerLiveReadOk,
    linkedIncidentIds: args.linkedIncidentIds,
    buyerLiveIncidents: args.items,
  });
  const membership = args.membership || { readOk: false, qualifyingPresent: null, qualifyingCount: null };
  const occupancy = membership.readOk ? membership.qualifyingCount : null;
  const entryDwellSeconds = resolveThresholdSeconds({
    zone: args.zone,
    envSeconds: args.envThresholdSeconds,
  });
  const occupiedForSeconds = dwellElapsedSeconds({
    occupancyStartedAtMs: args.occupancyStartedAtMs,
    nowMs,
    occupancy: membership.readOk && membership.qualifyingPresent ? (occupancy ?? 1) : 0,
  });
  const emptyForSeconds = args.emptySinceMs == null
    ? null
    : Math.max(0, (nowMs - args.emptySinceMs) / 1000);
  const zoneClear = parsePositiveSeconds(
    (args.zone as { zone_clear_dwell_seconds?: number | null } | null | undefined)?.zone_clear_dwell_seconds,
  );
  const phase = deriveShowcaseLivePhase({
    openMatch: false,
    joinUnavailable: false,
    identityReadOk: true,
    lifecycleReadOk: true,
    buyerLiveReadOk: true,
    membershipReadOk: membership.readOk,
    qualifyingPresent: membership.qualifyingPresent,
    occupiedForSeconds,
    emptyForSeconds,
    entryDwellSeconds,
    clearDwellSeconds: zoneClear ?? SHOWCASE_ZONE_CLEAR_DWELL_SECONDS,
    clearingFromOccupancy: args.clearingFromOccupancy,
  });
  return {
    phase,
    liveCanonicalActive: phase === 'active',
    occupancy,
    occupancyReadOk: membership.readOk,
    membershipReadOk: membership.readOk,
    qualifyingPresent: membership.qualifyingPresent,
    buyerLiveReadOk: args.buyerLiveReadOk,
    thresholdSeconds: entryDwellSeconds,
    dwellElapsedSeconds: occupiedForSeconds,
    zonePolygon: polygonFromZone(args.zone),
    zoneId: text(args.config.zoneId) || text(args.zone?.zone_id) || null,
    cameraId: text(args.config.cameraId) || null,
    matchingIncidentId: workflowIncidents.length === 1
      ? buyerLiveIncidentId(workflowIncidents[0]) || null
      : null,
    workflowStatus: workflowIncidents.length > 0 ? 'OPEN' : null,
    workflowOpenCount: workflowIncidents.length,
    areaName: text(args.zone?.name) || null,
    conditionId: text(args.conditionId) || null,
  };
}
